import { useMemo, useState, useEffect, useRef } from 'react';
import { useFetch as useFetch$1, useFetchCb as useFetchCb$1, useFetchMeta } from 'ruse-fetch';
import { useSelector, useDispatch } from 'react-redux';

/**
 * Converts an object to a query params string
 * @param params object with values
 * @returns a query string
 */
const queryString = (params) => {
    if (!params)
        return '';
    const q = Object.entries(params)
        .filter(([key, value]) => key && value != null && value !== '' && (!Array.isArray(value) || value.length > 0))
        .map(([key, value]) => encodeURIComponent(key) + '=' + encodeURIComponent(value))
        .join('&');
    return q.length ? '?' + q : '';
};
/**
 * Converts an ExtendedRequestInit into a plain RequestInit, updating the URL accordingly
 * @param url The base URL
 * @param opts The ExtendedRequestInit
 * @param token An optional Bearer token to add to the request
 * @returns The new URL and RequestInit
 */
const getExtendedOpts = (url, opts, token) => {
    const { params, body, ...other } = opts || {};
    const finalOpts = other;
    // Add the token
    if (token) {
        if (!finalOpts.headers)
            finalOpts.headers = {};
        if (!finalOpts.headers['Authorization']) {
            finalOpts.headers['Authorization'] = 'Bearer ' + token;
        }
    }
    // Add params to URL
    if (params)
        url += queryString(params);
    // If we got a body, default to POST
    if (body && !finalOpts.method)
        finalOpts.method = 'POST';
    // Detect JSON body and set the corresponding things
    if (body) {
        if (typeof body !== 'string' && !(body instanceof FormData)) {
            finalOpts.body = JSON.stringify(body);
            if (!finalOpts.headers)
                finalOpts.headers = {};
            finalOpts.headers['Content-Type'] = 'application/json';
        }
        else {
            finalOpts.body = body;
        }
    }
    return { url, opts: finalOpts };
};
const useFetch = (url, opts, key) => {
    const token = useSelector((s) => { var _a; return (_a = s === null || s === void 0 ? void 0 : s.user) === null || _a === void 0 ? void 0 : _a.token; });
    const { url: eUrl, opts: eOpts } = getExtendedOpts(url, opts, token);
    return useFetch$1(eUrl, eOpts, key);
};
const useFetchCb = (urlA, optsA, keyA) => {
    const token = useSelector((s) => { var _a; return (_a = s === null || s === void 0 ? void 0 : s.user) === null || _a === void 0 ? void 0 : _a.token; });
    const fetchCb = useFetchCb$1();
    return (urlB, optsB, keyB) => {
        const { url: eUrl, opts: eOpts } = getExtendedOpts(urlB || urlA, optsB || optsA, token);
        return fetchCb(eUrl, eOpts, keyB || keyA);
    };
};
/**
 * Similar to useFetchCb but without dispatching anything
 */
const useFetchUncachedCb = () => {
    const token = useSelector((s) => { var _a; return (_a = s === null || s === void 0 ? void 0 : s.user) === null || _a === void 0 ? void 0 : _a.token; });
    return useMemo(() => (url, opts) => {
        const { url: eUrl, opts: eOpts } = getExtendedOpts(url, opts, token);
        return fetch(eUrl, eOpts).then(res => {
            const resType = res.headers.get('Content-Type');
            const isJson = resType && resType.startsWith('application/json');
            const valuePromise = isJson ? res.json() : res.text();
            return valuePromise
                .then(value => {
                if (res.ok) {
                    return value;
                }
                else {
                    const msg = 'Error ' + res.status;
                    const err = new Error(msg);
                    err.status = res.status;
                    err.payload = value;
                    throw err;
                }
            });
        });
    }, [token]);
};
/**
 * Helper to make "infinite scroll" pagination easier.
 */
const usePaginatedFetch = (url, opts) => {
    opts = opts || {};
    // Create defaults for the functions
    const defaultMerger = ((a, b) => [...a, ...b]);
    const defaultHasNext = (a => { var _a; return a.length === (((_a = opts.params) === null || _a === void 0 ? void 0 : _a.size) || 20); });
    // Grab all the settings, providing sane defaults
    const { params = {}, pageParam = 'page', merger = defaultMerger, hasNext = defaultHasNext, ...otherOpts } = opts;
    // Make sure opts.params does not contain a page parameter
    if (params[pageParam])
        delete params[pageParam];
    // First page load must use suspense
    const data = useFetch(url, { ...otherOpts, params: { ...params, [pageParam]: 0 } });
    const [state, setState] = useState(() => ({
        data,
        page: 0,
        isLoadingMore: false,
        hasNext: hasNext(data, data)
    }));
    useEffect(() => {
        // Reset state on data change
        setState({
            data,
            page: 0,
            isLoadingMore: false,
            hasNext: hasNext(data, data)
        });
    }, [data]);
    // Next pages load must be a callback, and override previous entries
    const nextOpts = { ...otherOpts, params: { ...params, [pageParam]: state.page + 1 } };
    const fetchNext = useFetchCb(url, nextOpts);
    const loadMore = () => {
        if (state.isLoadingMore)
            return Promise.resolve(state.data);
        setState({ ...state, isLoadingMore: true });
        return fetchNext()
            .then(nextPage => {
            const moreData = merger(state.data, nextPage);
            setState({
                data: moreData,
                page: state.page + 1,
                isLoadingMore: false,
                hasNext: hasNext(nextPage, moreData)
            });
            return moreData;
        })
            .catch(() => {
            setState({
                ...state,
                isLoadingMore: false,
                hasNext: false // If something went wrong, stop pagination?
            });
            return data;
        });
    };
    return {
        ...state,
        loadMore: (state.hasNext && !state.isLoadingMore) ? loadMore : null
    };
};
/**
 * Helper to easily call CRUD endpoints
 */
const useCRUD = (url) => {
    const fetch = useFetchUncachedCb();
    return {
        get: (id) => fetch(url + '/' + id),
        getList: () => fetch(url),
        create: (entity) => fetch(url, { body: entity }),
        update: (id, entity) => fetch(url + '/' + id, { body: entity, method: 'PUT' }),
        delete: (id) => fetch(url + '/' + id, { method: 'DELETE' }),
        custom: (urlSuffix, opts) => fetch(url + (urlSuffix || ''), opts)
    };
};
/**
 * Helper to make periodic calls to some endpoint, without intermediate loadings
 */
const usePeriodicFetch = (period, url, opts, key) => {
    const dispatch = useDispatch();
    const fetch = useFetchUncachedCb();
    const fetchRef = useRef(fetch);
    const returnValue = useFetch(url, opts, key);
    const meta = useFetchMeta(url, key);
    useEffect(() => {
        const id = setInterval(() => {
            fetchRef.current(url, opts)
                .then(value => dispatch({ type: 'useFetch/success', key: key || url, value, meta }));
        }, period);
        return () => clearInterval(id);
    }, [url, key]);
    useEffect(() => {
        fetchRef.current = fetch;
    }, [fetch]);
    return returnValue;
};

export { getExtendedOpts, queryString, useCRUD, useFetch, useFetchCb, useFetchUncachedCb, usePaginatedFetch, usePeriodicFetch };
